/**
 * OnboardingQuickStart
 *
 * The new ~30-second post-auth flow replacing the 10+ step legacy funnel.
 *
 *   Auth → quickstart → /dashboard
 *
 * Athlete branch: pick sport + class year → POST /onboarding/quickstart → setUser → /dashboard
 * Non-athlete branch:
 *   - With invite + family has athletes → pick which athlete → POST quickstart (athlete_profile_id)
 *   - With no invite OR invite + family is empty → enter athlete's name/sport/class_year inline
 *     → POST quickstart (athlete_first_name, athlete_last_name, sport, class_year)
 *
 * Spec: /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md §2 + §8 Phase 1 C1.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInvitation } from '../contexts/InvitationContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI, userAPI, familyAPI, User } from '../services/api';
import { User as UserIcon, Users, ArrowRight, Check } from 'react-feather';
import ErrorState from '../components/ErrorState';
import { TextInput, Select } from '../components/ui';
import analytics from '../services/analytics';

// Sports list. Softball is the only currently-supported sport; Volleyball is
// rendered disabled as a "coming soon" hint. Add more sports here as the
// product catalog expands — `enabled: false` shows them as preview-only pills.
const SPORTS: Array<{ name: string; enabled: boolean }> = [
  { name: 'Softball', enabled: true },
  { name: 'Volleyball', enabled: false },
];

// Softball positions. Kept tight (single-select for now; multi-select moves
// to profile edit later). Values must match the canonical position_name set
// the backend whitelists — see ALLOWED_POSITIONS_SOFTBALL in
// /web/backend/routes/onboarding.js. Order roughly mirrors recruiter intuition
// (battery + infield first, then outfield, then generic + utility).
const SOFTBALL_POSITIONS = [
  'Pitcher',
  'Catcher',
  'First Base',
  'Second Base',
  'Third Base',
  'Shortstop',
  'Outfielder',
  'Infielder',
  'Utility Player',
];

// US states + DC. 2-letter codes — athlete_profiles.state is VARCHAR(2).
const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

// Class years rendered as buttons. Range matches athlete_profiles
// check_graduation_year_valid CHECK (2025-2035) at DATABASE_DEFINITION.sql.
const CLASS_YEARS = (() => {
  const now = new Date().getFullYear();
  const start = Math.max(2025, now);
  const out: number[] = [];
  for (let y = start; y <= 2035; y++) out.push(y);
  return out;
})();

type Step = 'invited-combined' | 'submitting';
type UserKind = 'athlete' | 'non_athlete_ui';
// 'coach' never reaches POST /onboarding/quickstart. It only routes to /coach/signup.
type RoleChoice = UserKind | 'coach';

interface FamilyAthlete {
  id: string;
  firstName: string;
  lastName: string;
  sport: string;
  graduationYear: number;
}

const OnboardingQuickStart: React.FC = () => {
  const navigate = useNavigate();
  const { sessionToken, user, setUser, logout } = useAuth();
  const { invitationToken, invitationInfo, clearInvitation } = useInvitation();
  const toast = useToast();

  // One screen for everyone — invited or fresh. The combined view's role
  // toggle replaces the old standalone "Who are you?" pick-type step, and
  // the athlete picker / create form below auto-shapes itself based on
  // whether the family has existing athletes:
  //   - invitationInfo + family has athletes → role toggle + athlete picker
  //   - user.family_id set but no invitationInfo → returning invitee, same
  //   - no family yet (fresh signup) → role toggle + athlete create-form
  const [step, setStep] = useState<Step>('invited-combined');
  const [userKind, setUserKind] = useState<RoleChoice | null>(null);
  // A subset of POST /coach/register's rule: no user_type and no family.
  // /user/profile does not return athlete_profile_id or guardian_profile_id,
  // so a legacy account linked only through those passes here. The server
  // refuses it and CoachSignup shows its ACCOUNT_TYPE_CONFLICT screen.
  // Not keyed on the invitation, which can be a stale copy in localStorage.
  const profile = user as User | null;
  const canBecomeCoach = !!profile
    && !profile.user_type && !profile.family_id && !profile.athlete_profile_id;
  // On the invite-accept path the invitee already picked their role in
  // InvitationWelcome (backend set users.user_type), so pre-select the toggle
  // instead of making them choose 'Athlete / Helping' all over again. Fresh
  // signups have no user_type yet → leave it null so the toggle stays interactive.
  useEffect(() => {
    if (userKind !== null) return; // never override an in-progress selection
    const t = (user as any)?.user_type;
    if (t === 'athlete') setUserKind('athlete');
    else if (t === 'non_athlete_ui' || t === 'guardian') setUserKind('non_athlete_ui');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_type]);
  // Drop a coach pick if the account turns out to be family-linked.
  useEffect(() => {
    if (userKind === 'coach' && !canBecomeCoach) setUserKind(null);
  }, [userKind, canBecomeCoach]);
  const [sport, setSport] = useState<string>('Softball');
  const [classYear, setClassYear] = useState<number | null>(null);
  // Required at creation so matched-for-you returns rows on first dashboard
  // load. position is single-select for now; multi-select belongs on profile
  // edit. state is the athlete's home state (drives geo_score).
  const [position, setPosition] = useState<string>('');
  const [stateCode, setStateCode] = useState<string>('');

  // Non-athlete-with-invite branch state
  const [familyAthletes, setFamilyAthletes] = useState<FamilyAthlete[] | null>(null);
  const [loadingFamilyAthletes, setLoadingFamilyAthletes] = useState(false);
  // True when the family-athletes fetch fails. We MUST NOT silently fall
  // through to the create-athlete form on failure — an invited guardian whose
  // family already has an athlete would then create a DUPLICATE. Instead we
  // surface a retryable error and block the create/pick UI until it loads.
  const [familyAthletesError, setFamilyAthletesError] = useState(false);
  // Bumped by the retry button to re-run the fetch effect.
  const [familyAthletesReloadKey, setFamilyAthletesReloadKey] = useState(0);
  // The user picks one of the family's athletes here, then hits Continue.
  // Used to be auto-submit on row click — too easy to mis-tap on mobile,
  // and no way to back out without leaving the page.
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);

  // Non-athlete create-athlete branch state
  const [athleteFirstName, setAthleteFirstName] = useState('');
  const [athleteLastName, setAthleteLastName] = useState('');

  // APS-259. Apple sign-in often leaves the email address as users.name, and
  // the athlete's name is built from it. Ask for the person's own name then.
  const accountName = ((user as User | null)?.name || '').trim();
  const needsOwnName = !!user && (!accountName || accountName.includes('@'));
  const [ownFirstName, setOwnFirstName] = useState('');
  const [ownLastName, setOwnLastName] = useState('');
  const nameAskedRef = React.useRef(false);
  useEffect(() => {
    if (!needsOwnName || nameAskedRef.current) return;
    nameAskedRef.current = true;
    const hint = !accountName
      ? 'empty'
      : /privaterelay\.appleid\.com$/i.test(accountName) ? 'apple_relay' : 'email_name';
    try { analytics.track('quickstart_name_asked', { provider_hint: hint }); } catch {}
  }, [needsOwnName, accountName]);
  const ownNameError = (v: string): string | null => {
    const t = v.trim();
    if (!t) return null;
    if (t.includes('@')) return 'Use your name, not an email address.';
    if (t.length > 50) return 'Keep it under 50 characters.';
    return null;
  };

  // Already onboarded? Bounce.
  useEffect(() => {
    if (user && typeof (user as User).onboarding_step === 'number' && (user as User).onboarding_step! >= 100) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Fetch family's existing athletes whenever we have a family_id. Used by
  // the combined invited screen (so the picker is ready immediately when
  // the invitee picks "I'm helping") and by the legacy non-invited
  // non-athlete branch (which transitions to pick-athlete on type pick).
  //
  // Falls back to invitationInfo.family_id when user.family_id isn't set
  // yet — protects the combined-screen picker against any case where the
  // auto-accept hasn't completed (race, social-auth invite path regression,
  // network blip). The backend still verifies family access at quickstart
  // submit time, and the submit payload carries invite_token so the accept
  // happens then if it hasn't already.
  useEffect(() => {
    const fetchFamilyAthletes = async () => {
      const familyId = user?.family_id || invitationInfo?.family_id;
      if (!familyId) return;
      // Don't refetch — once we have a list (even empty) for this family,
      // we're done. Subsequent renders just re-use the state.
      if (familyAthletes !== null) return;
      setLoadingFamilyAthletes(true);
      setFamilyAthletesError(false);
      try {
        // When the user is already in the family (auto-accept completed
        // or returning invitee), use the authenticated family-profiles
        // endpoint. When they're not yet in the family (race window /
        // social-auth invite path before auto-accept fires), the
        // authenticated endpoint would 403 — use the invite-scoped
        // public endpoint instead, gated on having a valid token.
        let res: any;
        if (user?.family_id) {
          res = await athleteAPI.getFamilyProfiles(sessionToken!, user.family_id);
        } else if (invitationToken) {
          res = await familyAPI.getInvitationFamilyAthletes(invitationToken);
        } else {
          // Shouldn't happen — familyId came from invitationInfo so
          // invitationToken should also be set. Defensive empty list.
          setFamilyAthletes([]);
          setLoadingFamilyAthletes(false);
          return;
        }
        // Backend returns the raw athlete_profiles row (snake_case) despite
        // the API type claiming camelCase. Fall back through both shapes so
        // we render correctly regardless of which the backend returns.
        const list: FamilyAthlete[] = (res.profiles || []).map((p: any) => ({
          id: p.id,
          firstName: p.firstName || p.first_name || '',
          lastName: p.lastName || p.last_name || '',
          sport: p.sport,
          graduationYear: p.graduationYear || p.graduation_year || null,
        }));
        setFamilyAthletes(list);
      } catch (err) {
        // Do NOT setFamilyAthletes([]) here — an empty list reads as "family
        // has no athletes" and drops the user into the create-athlete form,
        // which is exactly how an invited guardian ends up creating a
        // duplicate athlete (#188). Leave the list null and flag the error so
        // the UI shows a retryable ErrorState instead.
        console.error('Failed to fetch family athletes:', err);
        setFamilyAthletesError(true);
      } finally {
        setLoadingFamilyAthletes(false);
      }
    };
    fetchFamilyAthletes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKind, sessionToken, user?.family_id, invitationInfo?.family_id, invitationToken, familyAthletesReloadKey]);

  // Retry handler for the family-athletes fetch. Resets the error flag and
  // bumps the reload key so the effect re-runs (familyAthletes is still null
  // after a failure, so the "already loaded" guard won't short-circuit it).
  const retryFamilyAthletes = () => {
    setFamilyAthletesError(false);
    setFamilyAthletesReloadKey((k) => k + 1);
  };

  const submit = async (payload: Parameters<typeof userAPI.quickStart>[1]) => {
    if (!sessionToken) {
      toast.error('Session expired. Please sign in again.');
      return;
    }
    // Remember where we came from so the catch handler can put the user
    // back on the same screen with their picks intact. The old fallback
    // tossed invited-combined users onto athlete-details or create-athlete
    // and lost their role + picker selection.
    const previousStep = step;
    setStep('submitting');
    try {
      const updated = await userAPI.quickStart(sessionToken, payload);
      if (!('already_onboarded' in updated)) {
        // Push fresh user state into AuthContext so guards downstream see the
        // new user_type / family_id / onboarding_step rather than stale nulls.
        setUser(updated as User);
      }
      // Clear the invitation token now that onboarding is fully complete.
      // SignUpPage / LoginPage deliberately don't clear before routing here
      // so the invited-combined screen can read invitationInfo. This is the
      // canonical clear point.
      if (invitationToken) clearInvitation();
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Quickstart failed:', err);
      toast.error(err?.message || 'Could not finish setup. Try again.');
      setStep(previousStep);
    }
  };

  // ----- Render -----
  return (
    <div style={shell}>
      <div style={card}>
        <div style={header}>
          <div style={{ flex: 1 }}>
            <h1 style={headerTitle}>Let's get you in</h1>
            <p style={headerSub}>
              {invitationInfo
                ? `You were invited to join the ${invitationInfo.family_name} family.`
                : "A couple of quick questions and you're using the product."}
            </p>
          </div>
          {/* Sign-out escape hatch — without this, a user who hits quickstart
              and decides to bail (wrong account, abandoning signup, etc.) has
              no way out except closing the tab. */}
          <button
            type="button"
            onClick={() => { logout().catch(() => {}); }}
            style={signOutLink}
          >
            Sign out
          </button>
        </div>

        <div style={body}>
          {step === 'invited-combined' && (() => {
            // Unified flow: role toggle on top, then ONE athlete-
            // selection-or-creation UI regardless of role. The athlete
            // who arrives to find their parent already made their
            // profile picks that existing row instead of duplicating;
            // the guardian who arrives to find no athlete yet creates
            // one. Same UI for both — backend's user_type field is
            // what distinguishes how the user is linked to the athlete.
            const hasAthletes = (familyAthletes || []).length > 0;
            const isAddingNew = !hasAthletes || selectedAthleteId === '__new__';
            // The athlete's own name is derived from their signed-in user row on the
            // backend (the 'athlete' branch never reads athlete_first_name/last_name —
            // only the guardian/'non_athlete_ui' branch does), so only require + show
            // the name inputs for the "I'm helping" role.
            const needsAthleteName = userKind === 'non_athlete_ui';
            const newAthleteValid = (!needsAthleteName || (!!athleteFirstName.trim() && !!athleteLastName.trim()))
              && !!sport && !!classYear && !!position && !!stateCode;
            const existingAthleteValid = !!selectedAthleteId && selectedAthleteId !== '__new__';
            const athleteValid = isAddingNew ? newAthleteValid : existingAthleteValid;
            const isCoach = userKind === 'coach';
            const ownNameValid = !needsOwnName || (
              !!ownFirstName.trim() && !!ownLastName.trim()
              && !ownNameError(ownFirstName) && !ownNameError(ownLastName)
            );
            const ownNamePayload = needsOwnName
              ? { user_first_name: ownFirstName.trim(), user_last_name: ownLastName.trim() }
              : {};
            // The athlete section waits for a family role only on the screen
            // that offers "I'm a College Coach" and carries no invitation, so a
            // coach is not shown an athlete form first. Every other screen is
            // as before: a preselected role, a family-linked account (no coach
            // card) and an invitee all see it straight away.
            const showAthleteSection = userKind === 'athlete' || userKind === 'non_athlete_ui'
              || (userKind === null && (!canBecomeCoach || !!invitationInfo || !!invitationToken));
            const valid = isCoach || (!!userKind && athleteValid && ownNameValid);
            const handleSubmit = () => {
              if (userKind === 'coach') {
                // Writes nothing here. Coach sign-up owns registration.
                navigate('/coach/signup');
                return;
              }
              if (!userKind || !athleteValid || !ownNameValid) return;
              if (existingAthleteValid) {
                // Existing athlete — link by id. Backend handles both
                // roles correctly via user_type.
                submit({
                  user_type: userKind,
                  athlete_profile_id: selectedAthleteId!,
                  invite_token: invitationToken || undefined,
                  ...ownNamePayload,
                } as any);
              } else {
                submit({
                  user_type: userKind,
                  athlete_first_name: athleteFirstName.trim(),
                  athlete_last_name: athleteLastName.trim(),
                  sport,
                  class_year: classYear!,
                  position,
                  state: stateCode,
                  invite_token: invitationToken || undefined,
                  ...ownNamePayload,
                } as any);
              }
            };
            return (
            <>
              {needsOwnName && !isCoach && (
                <>
                  <h2 style={sectionTitle}>What's your name?</h2>
                  <label htmlFor="qs-own-first" style={fieldLabel}>Your first name</label>
                  <TextInput
                    id="qs-own-first"
                    type="text"
                    autoComplete="given-name"
                    maxLength={50}
                    required
                    invalid={!!ownNameError(ownFirstName)}
                    aria-describedby={ownNameError(ownFirstName) ? 'qs-own-first-error' : undefined}
                    value={ownFirstName}
                    onChange={(e) => setOwnFirstName(e.target.value)}
                    style={textInput}
                  />
                  {ownNameError(ownFirstName) && (
                    <p id="qs-own-first-error" role="alert" style={fieldError}>{ownNameError(ownFirstName)}</p>
                  )}
                  <label htmlFor="qs-own-last" style={fieldLabel}>Your last name</label>
                  <TextInput
                    id="qs-own-last"
                    type="text"
                    autoComplete="family-name"
                    maxLength={50}
                    required
                    invalid={!!ownNameError(ownLastName)}
                    aria-describedby={ownNameError(ownLastName) ? 'qs-own-last-error' : undefined}
                    value={ownLastName}
                    onChange={(e) => setOwnLastName(e.target.value)}
                    style={textInput}
                  />
                  {ownNameError(ownLastName) && (
                    <p id="qs-own-last-error" role="alert" style={fieldError}>{ownNameError(ownLastName)}</p>
                  )}
                  <div style={{ height: 12 }} />
                </>
              )}
              <h2 style={sectionTitle}>Who are you?</h2>
              <p style={{ ...hint, marginTop: -4, marginBottom: 16 }}>
                {canBecomeCoach ? 'Pick your role.' : 'Pick your role, then choose or add the athlete.'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                <button
                  type="button"
                  aria-pressed={userKind === 'athlete'}
                  onClick={() => setUserKind('athlete')}
                  style={roleCard(userKind === 'athlete')}
                >
                  I'm the Athlete
                </button>
                <button
                  type="button"
                  aria-pressed={userKind === 'non_athlete_ui'}
                  onClick={() => setUserKind('non_athlete_ui')}
                  style={roleCard(userKind === 'non_athlete_ui')}
                >
                  I'm helping an Athlete
                </button>
                {/* Own row, so the two family cards keep their layout at 375px. */}
                {canBecomeCoach && (
                  <button
                    type="button"
                    aria-pressed={isCoach}
                    onClick={() => setUserKind('coach')}
                    style={roleCard(isCoach, '1 1 100%')}
                  >
                    I'm a College Coach
                  </button>
                )}
              </div>

              {isCoach && (
                <p style={{ ...hint, marginTop: -8, marginBottom: 8 }}>
                  Next, pick the college program you coach.
                </p>
              )}

              {/* Athlete picker — same for both roles. Picker rendered
                  whenever family has existing athletes AND user isn't
                  in the "add new" sub-state. Inline create form rendered
                  otherwise (family empty OR user chose Add New). */}
              {showAthleteSection && loadingFamilyAthletes && <p style={hint}>Loading family athletes…</p>}
              {showAthleteSection && !loadingFamilyAthletes && familyAthletesError && (
                <ErrorState
                  compact
                  message="We couldn't load your family's athletes. Retry first so you don't add the same athlete twice."
                  onRetry={retryFamilyAthletes}
                  style={{ marginBottom: 16 }}
                />
              )}
              {showAthleteSection && !loadingFamilyAthletes && !familyAthletesError && hasAthletes && !isAddingNew && (
                <>
                  <label style={fieldLabel}>
                    {userKind === 'athlete'
                      ? "Which athlete is you?"
                      : userKind === 'non_athlete_ui'
                        ? "Which athlete are you helping?"
                        : "Pick your athlete"}
                  </label>
                  {(familyAthletes || []).map((a) => {
                    const isSelected = selectedAthleteId === a.id;
                    const displayName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Athlete';
                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelectedAthleteId(a.id)}
                        style={{
                          ...athletePickRow,
                          borderColor: isSelected ? '#F9550D' : (athletePickRow as any).borderColor || '#E5E7EB',
                          background: isSelected ? 'rgba(249, 85, 13, 0.06)' : 'white',
                          boxShadow: isSelected ? '0 0 0 1px #F9550D' : 'none',
                        }}
                        aria-pressed={isSelected}
                      >
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{ fontWeight: 700, color: '#151133' }}>{displayName}</div>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>
                            {[a.sport, a.graduationYear ? `Class of ${a.graduationYear}` : ''].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {isSelected ? (
                          <div style={{
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#F9550D', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}><Check size={14} color="white" aria-label="Selected" /></div>
                        ) : (
                          <ArrowRight size={16} color="#9CA3AF" />
                        )}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setSelectedAthleteId('__new__')}
                    style={{ ...secondaryButton, marginTop: 12 }}
                  >
                    + Add a new athlete instead
                  </button>
                </>
              )}

              {showAthleteSection && !loadingFamilyAthletes && !familyAthletesError && isAddingNew && (
                <>
                  {hasAthletes && (
                    <button
                      onClick={() => setSelectedAthleteId(null)}
                      style={{
                        background: 'transparent', border: 'none',
                        color: '#6B7280', fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', padding: '4px 0', marginBottom: 8,
                        textAlign: 'left',
                      }}
                    >
                      ‹ Back to athlete list
                    </button>
                  )}
                  <label style={fieldLabel}>
                    {hasAthletes ? 'Add a new athlete' : 'Tell us about the athlete'}
                  </label>
                  {needsAthleteName && (
                    <>
                      {/* The heading above is a group label, not a field
                          label, so each box names itself for a screen reader. */}
                      <TextInput
                        type="text"
                        placeholder="Athlete first name"
                        aria-label="Athlete first name"
                        value={athleteFirstName}
                        onChange={(e) => setAthleteFirstName(e.target.value)}
                        style={textInput}
                      />
                      <TextInput
                        type="text"
                        placeholder="Athlete last name"
                        aria-label="Athlete last name"
                        value={athleteLastName}
                        onChange={(e) => setAthleteLastName(e.target.value)}
                        style={textInput}
                      />
                    </>
                  )}
                  <label style={fieldLabel}>Sport</label>
                  <div style={pillGrid}>
                    {SPORTS.map((s) => (
                      <button
                        key={s.name}
                        onClick={() => s.enabled && setSport(s.name)}
                        disabled={!s.enabled}
                        title={s.enabled ? undefined : 'Coming soon'}
                        style={!s.enabled ? pillDisabled : sport === s.name ? pillSelected : pillIdle}
                      >
                        {s.name}
                        {!s.enabled && <span style={comingSoonTag}>Coming soon</span>}
                      </button>
                    ))}
                  </div>
                  <label style={fieldLabel}>Class year</label>
                  <div style={pillGrid}>
                    {CLASS_YEARS.map((y) => (
                      <button key={y} onClick={() => setClassYear(y)} style={classYear === y ? pillSelected : pillIdle}>{y}</button>
                    ))}
                  </div>
                  <label style={fieldLabel}>Position</label>
                  <div style={pillGrid}>
                    {SOFTBALL_POSITIONS.map((p) => (
                      <button key={p} onClick={() => setPosition(p)} style={position === p ? pillSelected : pillIdle}>{p}</button>
                    ))}
                  </div>
                  <label htmlFor="qs-home-state" style={fieldLabel}>Home state</label>
                  {/* The bottom margin rides a wrapper rather than the control:
                      Select's chevron is positioned against its flex wrapper,
                      so a margin on the control itself would push the wrapper
                      taller and drop the chevron below the select's centre. */}
                  <div style={{ marginBottom: 20 }}>
                    <Select
                      id="qs-home-state"
                      value={stateCode}
                      onChange={(e) => setStateCode(e.target.value)}
                      style={selectInput}
                    >
                      <option value="">Select a state…</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ height: 8 }} />
                </>
              )}

              {(isCoach || !familyAthletesError) && (
                <button
                  onClick={handleSubmit}
                  disabled={!valid}
                  style={valid ? primaryButton : primaryButtonDisabled}
                >
                  Continue <ArrowRight size={16} style={{ marginLeft: 6 }} />
                </button>
              )}
            </>
            );
          })()}

          {step === 'submitting' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={spinner} />
              <p style={{ marginTop: 16, color: '#6B7280' }}>Setting up your account…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ----- Styles (inline, matches Manrope/#F9550D/#151133 palette across portal) -----
const shell: React.CSSProperties = {
  minHeight: '100vh',
  background: 'rgba(21, 17, 51, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  fontFamily: 'Manrope, sans-serif',
};
const card: React.CSSProperties = {
  background: 'white',
  borderRadius: 16,
  maxWidth: 560,
  width: '100%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  overflow: 'hidden',
};
const header: React.CSSProperties = {
  background: '#151133',
  padding: '28px 32px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
};
const signOutLink: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.6)',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'Manrope, sans-serif',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: 6,
  textDecoration: 'underline',
};
const headerTitle: React.CSSProperties = {
  margin: 0,
  color: 'white',
  fontSize: 22,
  fontWeight: 700,
};
const headerSub: React.CSSProperties = {
  margin: '6px 0 0 0',
  color: 'rgba(255,255,255,0.75)',
  fontSize: 14,
};
const body: React.CSSProperties = { padding: 28, background: '#fafbff' };
const sectionTitle: React.CSSProperties = {
  margin: '0 0 16px 0',
  color: '#151133',
  fontSize: 16,
  fontWeight: 600,
};
// "Who are you?" choice card. One style for all three so they match exactly.
const roleCard = (selected: boolean, flex = '1 1 0%'): React.CSSProperties => ({
  flex, padding: '14px 12px', border: '1.5px solid',
  borderColor: selected ? '#F9550D' : '#E5E7EB',
  borderRadius: 12,
  background: selected ? 'rgba(249, 85, 13, 0.06)' : 'white',
  cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
  fontWeight: 600, fontSize: 14, color: '#151133', textAlign: 'center',
});
const pillGrid: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 20,
};
const pillBase: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'Manrope, sans-serif',
};
const pillIdle: React.CSSProperties = {
  ...pillBase,
  background: 'white',
  color: '#151133',
  border: '1px solid rgba(21,17,51,0.2)',
};
const pillSelected: React.CSSProperties = {
  ...pillBase,
  background: '#F9550D',
  color: 'white',
  border: '1px solid #F9550D',
};
// Disabled / "Coming soon" sport pill — visually muted, click is a no-op
// (the disabled attribute also blocks programmatic click events).
const pillDisabled: React.CSSProperties = {
  ...pillBase,
  background: '#F3F4F6',
  color: 'rgba(21,17,51,0.45)',
  border: '1px dashed rgba(21,17,51,0.2)',
  cursor: 'not-allowed',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};
const comingSoonTag: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  padding: '2px 6px',
  borderRadius: 999,
  background: 'rgba(249, 85, 13, 0.12)',
  color: '#F9550D',
};
const athletePickRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  background: 'white',
  border: '1px solid rgba(21,17,51,0.15)',
  borderRadius: 12,
  padding: '14px 18px',
  marginBottom: 8,
  cursor: 'pointer',
  fontFamily: 'Manrope, sans-serif',
};
const primaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '14px 18px',
  fontSize: 15,
  fontWeight: 700,
  background: '#F9550D',
  color: 'white',
  border: 'none',
  borderRadius: 12,
  cursor: 'pointer',
  marginTop: 8,
  fontFamily: 'Manrope, sans-serif',
  boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
};
const primaryButtonDisabled: React.CSSProperties = {
  ...primaryButton,
  background: '#D1D5DB',
  cursor: 'not-allowed',
  boxShadow: 'none',
};
const secondaryButton: React.CSSProperties = {
  ...primaryButton,
  background: 'white',
  color: '#151133',
  border: '1px solid rgba(21,17,51,0.2)',
  boxShadow: 'none',
};
// Only the stack rhythm now — the box itself (width, padding, border, focus,
// invalid, disabled) belongs to the TextInput primitive.
const textInput: React.CSSProperties = {
  marginBottom: 12,
};
// All of the native-chrome reset this used to carry — appearance, the inline
// data-URI chevron, the padding reserved for it, the outline suppression — is
// the Select primitive's job now, chevron included. What is left is the one
// thing this screen wants that the primitive does not assume: the medium weight
// the quickstart's fields are set in.
const selectInput: React.CSSProperties = {
  fontWeight: 500,
  lineHeight: 1.4,
};
const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#151133',
  marginTop: 4,
  marginBottom: 8,
};
const hint: React.CSSProperties = { color: '#6B7280', fontSize: 13 };
const fieldError: React.CSSProperties = {
  color: 'var(--aps-danger)',
  fontSize: 13,
  fontWeight: 600,
  margin: '-6px 0 12px',
};
const spinner: React.CSSProperties = {
  width: 32,
  height: 32,
  border: '3px solid rgba(249, 85, 13, 0.2)',
  borderTop: '3px solid #F9550D',
  borderRadius: '50%',
  margin: '0 auto',
  animation: 'qsspin 1s linear infinite',
};

// Inject the spinner keyframes (one-time, idempotent). The .qs-select focus
// rule that used to live here went with the native select: it set
// `outline: none` and drew its own box-shadow ring, which is exactly the
// one-off-per-screen focus treatment the Select primitive exists to end.
if (typeof document !== 'undefined' && !document.getElementById('qsspin-style')) {
  const s = document.createElement('style');
  s.id = 'qsspin-style';
  s.textContent = `@keyframes qsspin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}

export default OnboardingQuickStart;
